import type { LocalDatabase } from '../core/types';
import type { QueryBuilderSpec } from '~/powersync/hooks/useLivePowerSyncQuery';

export const MealLogQueries = {
  /**
   * Find all meal logs within a date range
   * Includes joined meal and nutritional information
   * User filtering is handled by PowerSync sync rules
   *
   * Build the bounds with `ConsumedDateRange` — they are a deliberate SUPERSET
   * of the wanted days, so every caller MUST narrow the result with
   * `ConsumedDateRange.filterToDays` before rendering or aggregating.
   */
  findByDateRange: (
    fromDate: string,
    toDate: string,
  ): QueryBuilderSpec<unknown> => ({
    createQuery: (db: LocalDatabase) =>
      db
        .selectFrom('MealLog')
        .select([
          'MealLog.id',
          'MealLog.userId',
          'MealLog.niceId',
          'MealLog.mealId',
          'MealLog.consumedAmount',
          'MealLog.consumedAmountUnit',
          'MealLog.consumedAt',
          'MealLog.mealType',
          'MealLog.createdAt',
          'MealLog.updatedAt',
        ])
        .where('MealLog.consumedAt', '>=', fromDate)
        .where('MealLog.consumedAt', '<=', toDate)
        .orderBy('MealLog.consumedAt', 'desc'),
    queryKey: ['meal-log', 'dateRange', fromDate, toDate],
  }),

  /**
   * Find a meal log by ID
   */
  findById: (id: string): QueryBuilderSpec<unknown> => ({
    createQuery: (db: LocalDatabase) =>
      db
        .selectFrom('MealLog')
        .select([
          'MealLog.id',
          'MealLog.userId',
          'MealLog.niceId',
          'MealLog.mealId',
          'MealLog.consumedAmount',
          'MealLog.consumedAmountUnit',
          'MealLog.consumedAt',
          'MealLog.mealType',
          'MealLog.createdAt',
          'MealLog.updatedAt',
        ])
        .where('MealLog.id', '=', id)
        .limit(1),
    queryKey: ['meal-log', 'id', id],
  }),

  /**
   * Find multiple meal logs by their IDs
   */
  findByIds: (ids: string[]): QueryBuilderSpec<unknown> => ({
    createQuery:
      ids.length > 0
        ? (db: LocalDatabase) =>
            db
              .selectFrom('MealLog')
              .select([
                'MealLog.id',
                'MealLog.userId',
                'MealLog.niceId',
                'MealLog.mealId',
                'MealLog.consumedAmount',
                'MealLog.consumedAmountUnit',
                'MealLog.consumedAt',
                'MealLog.mealType',
                'MealLog.createdAt',
                'MealLog.updatedAt',
              ])
              .where('MealLog.id', 'in', ids)
        : null,
    queryKey: ['meal-log', 'ids', ...ids],
  }),

  /**
   * The user's most-logged meals, most-frequent first. Groups every meal log by
   * `mealId` and counts occurrences — used by the right-rail "favourites" list.
   * All-time and global (PowerSync sync rules already scope rows to the user).
   * Returns `{ mealId, logCount }`; the caller resolves meal details separately.
   */
  findMostLogged: (limit = 5): QueryBuilderSpec<unknown> => ({
    createQuery: (db: LocalDatabase) =>
      db
        .selectFrom('MealLog')
        .select((eb) => [
          'MealLog.mealId',
          eb.fn.count('MealLog.id').as('logCount'),
        ])
        .where('MealLog.mealId', 'is not', null)
        .groupBy('MealLog.mealId')
        .orderBy((eb) => eb.fn.count('MealLog.id'), 'desc')
        .limit(limit),
    queryKey: ['meal-log', 'mostLogged', limit],
  }),

  /**
   * Find a meal log by niceId (for edit mode)
   */
  findByNiceId: (
    niceId: string | null | undefined,
  ): QueryBuilderSpec<unknown> => ({
    createQuery: niceId
      ? (db: LocalDatabase) =>
          db
            .selectFrom('MealLog')
            .select([
              'MealLog.id',
              'MealLog.userId',
              'MealLog.niceId',
              'MealLog.mealId',
              'MealLog.consumedAmount',
              'MealLog.consumedAt',
              'MealLog.mealType',
              'MealLog.createdAt',
              'MealLog.updatedAt',
            ])
            .where('MealLog.niceId', '=', niceId)
            .limit(1)
      : null,
    queryKey: ['meal-log', 'niceId', niceId ?? 'null'],
  }),
};
